/**
 * Routing Event Store
 * 
 * Append-only, in-memory event store for routing decisions.
 * This is a system ledger, not a UI store.
 * 
 * PRINCIPLES:
 * - Append-only (no mutations of past events)
 * - No business logic inside the store
 * - Supports both SIMULATION and LIVE sources
 * - Thread-safe for single-threaded JS runtime
 */

import type {
  RoutingEvent,
  RoutingEventSource,
  RoutingEventOutcome,
  RoutingEventFilter,
  RoutingEventStats,
} from './types';

// --- In-Memory Store ---

let events: RoutingEvent[] = [];
let eventsSnapshot: RoutingEvent[] = []; // Stable reference for useSyncExternalStore
let listeners: Array<() => void> = [];

// --- Core Operations ---

/**
 * Record a routing event (append-only)
 * This is the ONLY way to add events to the store.
 */
export function recordRoutingEvent(event: RoutingEvent): void {
  // Validate event before recording
  if (!event.id || !event.lead?.id || !event.outcome) {
    console.error('[RoutingEventStore] Invalid event, skipping:', event);
    return;
  }
  
  // Append to store (immutable push)
  events = [...events, event];
  // Update stable snapshot reference
  eventsSnapshot = events;
  
  // Notify listeners
  notifyListeners();
  
  // Debug logging in development
  if (import.meta.env.MODE !== 'production') {
    console.log(`[RoutingEventStore] Recorded ${event.source} event:`, {
      id: event.id,
      outcome: event.outcome,
      leadId: event.lead.id,
      winner: event.winner?.agentName,
    });
  }
}

/**
 * Get all routing events (read-only snapshot for useSyncExternalStore)
 * Returns a STABLE REFERENCE - only changes when events are added/removed
 */
export function getAllRoutingEvents(): RoutingEvent[] {
  return eventsSnapshot;
}

/**
 * Get recent routing events with optional limit
 */
export function getRecentRoutingEvents(limit: number = 50): RoutingEvent[] {
  // Return most recent first
  return [...events].reverse().slice(0, limit);
}

/**
 * Get routing events filtered by criteria
 */
export function getFilteredRoutingEvents(filter: RoutingEventFilter): RoutingEvent[] {
  return events.filter(event => {
    if (filter.source && event.source !== filter.source) return false;
    if (filter.outcome && event.outcome !== filter.outcome) return false;
    if (filter.leadType && event.lead.type !== filter.leadType) return false;
    if (filter.leadState && event.lead.state !== filter.leadState) return false;
    if (filter.agentId && event.winner?.agentId !== filter.agentId) return false;
    if (filter.simulationRunId && event.metadata.simulationRunId !== filter.simulationRunId) return false;
    if (filter.fromTimestamp && event.timestamp < filter.fromTimestamp) return false;
    if (filter.toTimestamp && event.timestamp > filter.toTimestamp) return false;
    return true;
  });
}

/**
 * Get routing events by source
 */
export function getRoutingEventsBySource(source: RoutingEventSource): RoutingEvent[] {
  return events.filter(e => e.source === source);
}

/**
 * Get routing events by outcome
 */
export function getRoutingEventsByOutcome(outcome: RoutingEventOutcome): RoutingEvent[] {
  return events.filter(e => e.outcome === outcome);
}

/**
 * Get routing events by simulation run ID
 */
export function getRoutingEventsBySimulationRun(simulationRunId: string): RoutingEvent[] {
  return events.filter(e => e.metadata.simulationRunId === simulationRunId);
}

/**
 * Clear all events (for simulation reset only)
 * 
 * WARNING: This clears the entire store. Only use for simulation resets.
 */
export function clearEvents(): void {
  events = [];
  eventsSnapshot = [];
  notifyListeners();
  
  if (import.meta.env.MODE !== 'production') {
    console.log('[RoutingEventStore] All events cleared');
  }
}

/**
 * Clear events by source (for simulation reset without affecting live events)
 */
export function clearEventsBySource(source: RoutingEventSource): void {
  const before = events.length;
  events = events.filter(e => e.source !== source);
  eventsSnapshot = events;
  const removed = before - events.length;
  
  notifyListeners();
  
  if (import.meta.env.MODE !== 'production') {
    console.log(`[RoutingEventStore] Cleared ${removed} ${source} events`);
  }
}

// --- Statistics ---

/**
 * Get event statistics
 */
export function getRoutingEventStats(): RoutingEventStats {
  const total = events.length;
  
  const byOutcome: Record<RoutingEventOutcome, number> = {
    ROUTED: 0,
    UNSOLD: 0,
    RETRY_QUEUED: 0,
    STOCKED: 0,
    VALIDATION_FAILED: 0,
  };
  
  const bySource: Record<RoutingEventSource, number> = {
    SIMULATION: 0,
    LIVE: 0,
  };
  
  let totalDuration = 0;
  
  for (const event of events) {
    byOutcome[event.outcome]++;
    bySource[event.source]++;
    totalDuration += event.metadata.durationMs;
  }
  
  const routingSuccessRate = total > 0 
    ? (byOutcome.ROUTED / total) * 100 
    : 0;
  
  const averageDurationMs = total > 0 
    ? totalDuration / total 
    : 0;
  
  return {
    total,
    byOutcome,
    bySource,
    routingSuccessRate,
    averageDurationMs,
  };
}

// --- Subscription (for React integration) ---

/**
 * Subscribe to store changes
 * Returns an unsubscribe function
 */
export function subscribeToRoutingEvents(listener: () => void): () => void {
  listeners.push(listener);
  return () => {
    listeners = listeners.filter(l => l !== listener);
  };
}

/**
 * Get current event count (for React useSyncExternalStore)
 */
export function getRoutingEventCount(): number {
  return events.length;
}

/**
 * Notify all listeners of store changes
 */
function notifyListeners(): void {
  for (const listener of listeners) {
    listener();
  }
}

// --- Snapshot for debugging ---

/**
 * Get a snapshot of the store state for debugging
 */
export function getStoreSnapshot(): {
  totalEvents: number;
  stats: RoutingEventStats;
  recentEvents: RoutingEvent[];
} {
  return {
    totalEvents: events.length,
    stats: getRoutingEventStats(),
    recentEvents: getRecentRoutingEvents(10),
  };
}
